<template>
  <v-card color="systemPrimary" class="rounded-lg mb-4 d-flex flex-column align-start pa-4">
    <div class="d-flex full-width align-center justify-space-between mb-4">
      <Typography variant="h-5" as="h5" class="word-break white--text"> Redeem </Typography>
      <v-progress-circular
        v-if="userBalanceLoading"
        indeterminate
        color="white"
        size="25"
        width="2"
        class="ml-3 pa-1"
      />
      <Typography v-else variant="h-5" as="h5" class="word-break white--text">
        {{ formatCurrency(userBalance, rewardsOverrideAward, true) }}
      </Typography>
    </div>

    <!-- [175387]
    NOTE: Hide transfer points to card option for now since it's not fully ready and we don't want to cause confusion.
    See https://dev.azure.com/One10Corp/PerformX%20V4/_workitems/edit/175387/ for more details. 
    We can re-enable this in the future once it's ready.
    <WalletTileItem
      class="mb-4"
      v-if="isOne10PointsToCardEnabled()"
      icon="mdi-credit-card"
      color="brandPrimary"
      title="Transfer points to card"
      @click="$emit('open-transfer-points', userBalance)"
    /> 
    -->
    <WalletTileItem
      icon="mdi-store"
      color="brandPrimary"
      title="Redeem at marketplace"
      :disabled="isCatalogDisabled()"
      :loading="userCatalogUrlLoading"
      :expandable="showTaxInfoGate"
      :default-expanded="showTaxInfoGate"
      @click="onCatalogUrlClick"
    >
      <template #expanded-content>
        <div
          class="tax-info-prompt mt-4 pa-6 rounded d-flex flex-column align-center text-center full-width"
        >
          <Typography variant="h-5" as="h3"> Add tax info to redeem </Typography>
          <Typography variant="body-1" as="p" class="mt-2 mb-0"> Takes about a minute. </Typography>
          <Button color="brandPrimary" class="mt-4" @click.stop="onAddTaxInfo">
            ADD TAX INFO
          </Button>
        </div>
      </template>
    </WalletTileItem>
    <AddressFormModal
      :visible="showParticipantAddressForm"
      @update:visible="showParticipantAddressForm = $event"
      @saved="onAddressSaved"
    />
    <TaxInfoRequiredModal
      :visible="showTaxInfoRequiredModal"
      @update:visible="showTaxInfoRequiredModal = $event"
      @add-tax-info="onAddTaxInfo"
    />
    <TaxInfoFormDrawer
      :visible="showTaxInfoForm"
      @update:visible="onTaxInfoDrawerVisibilityChange"
      @saved="onTaxInfoSaved"
    />
  </v-card>
</template>

<script>
// Packages
import { mapState } from "vuex";

// Components
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import AddressFormModal from "@/components/wallet/rewards/AddressFormModal.vue";
import TaxInfoFormDrawer from "@/components/wallet/rewards/TaxInfoFormDrawer.vue";
import TaxInfoRequiredModal from "@/components/wallet/rewards/TaxInfoRequiredModal.vue";
import WalletTileItem from "@/components/wallet/WalletTileItem.vue";

// Services
import WalletService from "@/services/WalletService";

//  Mixins
import formatCurrencyMixin from "@/mixins/formatCurrency";
import featureFlagsMixin from "@/mixins/featureFlags";
import taxInfoGateMixin from "@/mixins/taxInfoGate";

// Data
import { rewardsOverrideAward } from "@/shared_data/data";

export default {
  name: "RedeemRewards",
  components: {
    Typography,
    Button,
    AddressFormModal,
    TaxInfoFormDrawer,
    TaxInfoRequiredModal,
    WalletTileItem,
  },
  mixins: [formatCurrencyMixin, featureFlagsMixin, taxInfoGateMixin],
  data() {
    return {
      userBalance: 0,
      userBalanceLoading: true,
      userBalanceError: false,
      userCatalogUrl: "",
      userCatalogUrlLoading: false,
      userCatalogUrlError: false,
      showParticipantAddressForm: false,
      showTaxInfoForm: false,
      showTaxInfoRequiredModal: false,
      // Set when the drawer reports a save, consumed when the user dismisses its
      // confirmation - that is when the released points are worth re-reading.
      taxInfoSavedPendingRefresh: false,
      rewardsOverrideAward,
    };
  },
  async created() {
    this.getRewardsUserBalance();
    // Resolved up front so the tile can render as expandable rather than
    // deciding on click.
    this.checkTaxInfoStatus();
  },
  methods: {
    onCatalogUrlError() {
      this.$store.dispatch("showError", {
        title: "Error opening marketplace",
        message: "Please try again later or contact support if the problem persists.",
      });
    },
    onBalanceError() {
      this.$store.dispatch("showError", {
        title: "Error getting points balance",
        message: "Please refresh the page to try again or contact support if the problem persists.",
      });
    },
    isAddressError(status, message = "") {
      return status === 400 && message.includes("Participant address is incomplete");
    },
    isTaxInfoError(status, message = "") {
      return status === 400 && /tax info|tax information|w-?9/i.test(message);
    },
    isRewardRequirementError(status, message = "") {
      return (
        this.isAddressError(status, message) ||
        (this.canCollectTaxInfo && this.isTaxInfoError(status, message))
      );
    },
    isCatalogDisabled() {
      return this.userBalanceLoading || this.userBalanceError || this.userCatalogUrlLoading;
    },
    async onAddressSaved() {
      const url = await this.getRewardsUserCatalogUrlWithRetry();
      if (url) {
        window.open(url, "_blank");
      }
    },
    // The drawer shows its own success screen, so redemption is not resumed automatically.
    async onTaxInfoSaved() {
      // Card issuance is handled by the backend saveTaxInfo endpoint.
      // Clearing the gate is what lets the tile redeem again: onCatalogUrlClick
      // returns early while the gate is up, so leaving it set would strand the
      // user on the inline prompt until a page reload.
      this.clearTaxInfoGate();

      // Deliberately no catalog open here. The drawer switches to its own "Tax
      // Info Submitted!" confirmation on save, and sending the user straight to
      // the marketplace throws that away and moves them somewhere they did not
      // ask to go. They dismiss the confirmation themselves, and the refresh
      // below runs then.
      this.taxInfoSavedPendingRefresh = true;
    },
    onTaxInfoDrawerVisibilityChange(visible) {
      this.showTaxInfoForm = visible;

      // Saving tax info releases points that were held pending it, so the balance
      // on this card is stale from that moment. Refreshing when the confirmation
      // is dismissed picks the released points up without interrupting it.
      if (!visible && this.taxInfoSavedPendingRefresh) {
        this.taxInfoSavedPendingRefresh = false;
        this.$store.dispatch("refreshWallet");
        this.getRewardsUserBalance();
        this.broadcastTaxInfoRefresh();
      }
    },
    // Tax info captured in another part of the wallet - the card drawer - releases
    // points held pending it just the same, so this balance is stale even though
    // the save did not happen here.
    onTaxInfoCapturedElsewhere() {
      this.getRewardsUserBalance();
    },
    onAddTaxInfo() {
      if (!this.canCollectTaxInfo) {
        return;
      }

      this.showTaxInfoRequiredModal = false;
      this.showTaxInfoForm = true;
    },
    async onCatalogUrlClick() {
      // When tax info is outstanding the tile expands to the inline prompt instead of redeeming.
      if (this.showTaxInfoGate) {
        return;
      }

      // Re-checked on click rather than trusted from mount: tax info can be
      // captured elsewhere (the profile page, another tab) after this component
      // loaded, and redeeming is the expensive thing to get wrong.
      //
      // `showTaxInfoGate` pairs two deliberately separate conditions:
      //   1. canCollectTaxInfo - global `feature-tax-info` flag AND per-client
      //      `Client.taxInfoEnabled` master switch. The LD flag is a kill switch
      //      for the whole feature; the client flag is only ever true when the
      //      tenant has fully set up their WIR/DataVault config, so calling the
      //      backend when it's off would 400 with "not configured".
      //   2. taxInfoRequired - a per-user, server-driven decision from
      //      microservice-user. It's kept separate (not folded into the flag) so
      //      the "who must provide tax info" business rule can evolve server-side
      //      (e.g. only above an earnings threshold, only certain reward types)
      //      without a client change or a flag flip.
      // Both must hold to block: feature+client on AND this user still owes tax info.
      this.userCatalogUrlLoading = true;
      try {
        await this.checkTaxInfoStatus();
      } finally {
        this.userCatalogUrlLoading = false;
      }

      if (this.showTaxInfoGate) {
        this.showTaxInfoRequiredModal = true;
        return;
      }
      const url = await this.getRewardsUserCatalogUrl();
      if (url) {
        window.open(url, "_blank");
      }
    },
    async getRewardsUserBalance() {
      try {
        this.userBalanceLoading = true;
        const response = await WalletService.getRewardsUserBalanceV2(
          this.userProfile.userId,
          this.magicLinkToken
        );
        this.userBalance = response;
      } catch (error) {
        console.log("Error getting rewards user balance: ", error);
        this.userBalance = 0;
        this.userBalanceError = true;
        this.onBalanceError();
      } finally {
        this.userBalanceLoading = false;
      }
    },
    async getRewardsUserCatalogUrl() {
      this.userCatalogUrlLoading = true;
      try {
        const response = await WalletService.getRewardsUserCatalogUrlV2(
          this.userProfile.userId,
          this.magicLinkToken
        );
        this.userCatalogUrl = response.url;
        return response.url;
      } catch (error) {
        const status = error?.status;
        const message = error?.data?.message || "";

        if (this.isAddressError(status, message)) {
          this.showParticipantAddressForm = true;
        } else if (this.canCollectTaxInfo && this.isTaxInfoError(status, message)) {
          this.showTaxInfoRequiredModal = true;
        } else {
          this.userCatalogUrl = null;
          this.userCatalogUrlError = true;
          this.onCatalogUrlError();
        }
      } finally {
        this.userCatalogUrlLoading = false;
      }
    },
    async getRewardsUserCatalogUrlWithRetry() {
      const MAX_RETRIES = 5;
      this.userCatalogUrlLoading = true;

      for (let i = 0; i < MAX_RETRIES; i++) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        try {
          const response = await WalletService.getRewardsUserCatalogUrlV2(
            this.userProfile.userId,
            this.magicLinkToken
          );
          if (response.url) {
            this.userCatalogUrl = response.url;
            this.userCatalogUrlLoading = false;
            return response.url;
          }
        } catch (error) {
          if (this.isRewardRequirementError(error?.status, error?.data?.message || "")) {
            continue;
          } else {
            this.userCatalogUrl = null;
            this.userCatalogUrlError = true;
            this.userCatalogUrlLoading = false;
            this.onCatalogUrlError();
            return null;
          }
        }
      }

      // If we get here, we've tried all retries and failed
      this.userCatalogUrl = null;
      this.userCatalogUrlError = true;
      this.userCatalogUrlLoading = false;
      this.onCatalogUrlError();
      return null;
    },
  },
  computed: {
    ...mapState(["userProfile", "magicLinkToken"]),
  },
};
</script>

<style scoped>
.tax-info-prompt {
  background-color: var(--v-brandCyanLight-base);
}
.list-slide-item {
  transition: all 0.5s;
  display: inline-block;
}
.list-slide-enter,
.list-slide-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
.list-slide-leave-active {
  position: absolute;
}
</style>
